import { apiService } from "./api";
import { API_CONFIG, buildUrl } from "../config/api";
import {
    Customer,
    CustomerAddress,
    CreateCustomerRequest,
    UpdateCustomerRequest,
    SearchParams,
    CustomerListResponse,
} from "../types";

export class CustomerService {
    // Customers
    async getCustomers(
        params?: SearchParams & {
            sort_by?: string;
            sort_order?: string;
        }
    ): Promise<CustomerListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
            queryParams.append("sort_order", params.sort_order);

        const url = `${
            API_CONFIG.ENDPOINTS.CUSTOMERS.BASE
        }?${queryParams.toString()}`;

        // Use the new paginated endpoint that returns metadata
        return apiService.get<CustomerListResponse>(url);
    }

    async exportCustomers(params?: {
        search?: string;
        active_only?: boolean;
        sort_by?: string;
        sort_order?: string;
    }): Promise<Blob> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
            queryParams.append("sort_order", params.sort_order);

        const url = `${
            API_CONFIG.ENDPOINTS.CUSTOMERS.BASE
        }/export/csv?${queryParams.toString()}`;
        return apiService.get<Blob>(url, { responseType: "blob" });
    }

    async getCustomerById(id: string): Promise<Customer> {
        return apiService.get<Customer>(
            `${API_CONFIG.ENDPOINTS.CUSTOMERS.BASE}/${id}`
        );
    }

    async getCustomerByEmail(email: string): Promise<Customer> {
        return apiService.get<Customer>(
            `${API_CONFIG.ENDPOINTS.CUSTOMERS.BY_EMAIL}/${encodeURIComponent(
                email
            )}`
        );
    }

    async createCustomer(data: CreateCustomerRequest): Promise<Customer> {
        return apiService.post<Customer>(
            API_CONFIG.ENDPOINTS.CUSTOMERS.BASE,
            data
        );
    }

    async updateCustomer(
        id: string,
        data: UpdateCustomerRequest
    ): Promise<Customer> {
        return apiService.put<Customer>(
            `${API_CONFIG.ENDPOINTS.CUSTOMERS.BASE}/${id}`,
            data
        );
    }

    async deleteCustomer(id: string): Promise<void> {
        return apiService.delete<void>(
            `${API_CONFIG.ENDPOINTS.CUSTOMERS.BASE}/${id}`
        );
    }

    /** Staff-initiated, no-OTP reset: mints a one-time link for staff to
     * copy and hand to the customer themselves — never shown again after
     * this response. */
    async generatePasswordResetLink(
        customerId: string
    ): Promise<{ reset_url: string; expires_at: string }> {
        return apiService.post<{ reset_url: string; expires_at: string }>(
            `${API_CONFIG.ENDPOINTS.CUSTOMERS.BASE}/${customerId}/reset-password`
        );
    }

    async searchCustomers(params: {
        q: string;
        skip?: number;
        limit?: number;
    }): Promise<Customer[]> {
        const queryParams = new URLSearchParams();
        queryParams.append("q", params.q);
        if (params.skip) queryParams.append("skip", params.skip.toString());
        if (params.limit) queryParams.append("limit", params.limit.toString());

        const url = `${
            API_CONFIG.ENDPOINTS.CUSTOMERS.SEARCH
        }?${queryParams.toString()}`;
        return apiService.get<Customer[]>(url);
    }

    // Customer Addresses
    async getCustomerAddresses(
        customerId: string,
        addressType?: "BILLING" | "SHIPPING"
    ): Promise<CustomerAddress[]> {
        const queryParams = new URLSearchParams();
        if (addressType) queryParams.append("address_type", addressType);

        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESSES, {
            id: customerId,
        });
        return apiService.get<CustomerAddress[]>(
            `${url}?${queryParams.toString()}`
        );
    }

    async getAddressById(id: string): Promise<CustomerAddress> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESS_DETAIL, { id });
        return apiService.get<CustomerAddress>(url);
    }

    async createAddress(
        customerId: string,
        data: Omit<
            CustomerAddress,
            "address_id" | "customer_id" | "create_time" | "update_time"
        >
    ): Promise<CustomerAddress> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESSES, {
            id: customerId,
        });
        return apiService.post<CustomerAddress>(url, data);
    }

    async updateAddress(
        id: string,
        data: Partial<CustomerAddress>
    ): Promise<CustomerAddress> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESS_DETAIL, { id });
        return apiService.put<CustomerAddress>(url, data);
    }

    async deleteAddress(id: string): Promise<void> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESS_DETAIL, { id });
        return apiService.delete<void>(url);
    }

    /** Marks this address as the default of its type (SHIPPING/BILLING) for
     * its customer, atomically demoting whichever address held that title
     * before — see AddressService.set_default_address on the backend. */
    async setDefaultAddress(id: string): Promise<CustomerAddress> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.CUSTOMERS.ADDRESS_SET_DEFAULT, { id });
        return apiService.post<CustomerAddress>(url);
    }
}

export const customerService = new CustomerService();
